import Avatar from 'assets/icons/avatar.svg';
import bulkUpload from 'assets/icons/PPSIcons/bulkUpload.svg';
import gsmsLogo from 'assets/logo/gsmsLogo.svg';
import { MODAL_TYPE } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import { Download } from 'lucide-react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect } from 'react';
import MenuPopOver from './components/menuPopover/index';
import NavbarLogic from './navbar.logic';
import classes from './navbar.module.scss';
import { usePpsAccess } from 'hooks/usePpsAccess';
import { UserInfoInterface } from 'Interfaces/User/userInterfaces';

// Move component definition outside of Navbar component
interface UserDetailsOverLayMenuContentProps {
  userMetadata: UserInfoInterface | null;
  logout: () => void;
}

const UserDetailsOverLayMenuContent: React.FC<UserDetailsOverLayMenuContentProps> = ({
  userMetadata,
  logout
}) => {
  return (
    <div>
      {userMetadata?.userInfo.name && (
        <span style={{ cursor: 'pointer' }} onClick={logout}>
          Logout
        </span>
      )}
    </div>
  );
};

const Navbar = (props: {
  heading: string;
  searchComponent?: React.ReactNode;
  progressComponent?: React.ReactNode;
}) => {
  const {
    NavbarStore: { isNavigationRestricted },
    GlobalModalStore: { toggleModal }
  } = useStore();

  const {
    handleNavigateToHomeScreen,
    isAuthenticated,
    userMetadata,
    logout,
    op,
    handleMenuClick,
    isUserInfoLoading
  } = NavbarLogic();
  const { canViewSysproJobs } = usePpsAccess();

  const handleBulkUploadModalOpen = () => {
    toggleModal(MODAL_TYPE.PPS_JPB_PASTE_BULK_UPLOAD_JOBS_MODAL);
  };

  const handleImportSysproJobsModalOpen = () => {
    toggleModal(MODAL_TYPE.PPS_JOB_PASTE_IMPORT_SYSPRO_JOBS_MODAL);
  };

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (isNavigationRestricted) {
        event.preventDefault();
        event.returnValue = '';
      }
      return ''; //Return a string to show a message
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isNavigationRestricted]);

  return (
    <nav className={classes.navbar}>
      <div className={classes.navbarLeftSidebar}>
        <MenuPopOver />
        <img className={classes.logo} onClick={handleNavigateToHomeScreen} src={gsmsLogo} />
      </div>

      <div className={classes.navbarRightSidebar}>
        <div className={classes.bartitle}>{props.heading}</div>
        {isAuthenticated && props.searchComponent}
        <div>{props.progressComponent}</div>

        <div className={classes.authInfoDiv}>
          {props.heading === 'PPS Scheduling Tool Enhancement' && (
            <>
              {canViewSysproJobs() && (
                <Button
                  onClick={handleImportSysproJobsModalOpen}
                  label="Import Syspro Jobs"
                  className={classes.bulkUploadButton}
                  icon={<Download size={16} style={{ marginRight: '0.5rem' }} />}
                />
              )}
              {canViewSysproJobs() && (
                <Button
                  onClick={handleBulkUploadModalOpen}
                  label="Bulk Upload"
                  className={classes.bulkUploadButton}
                  icon={
                    <img src={bulkUpload} alt="Bulk Upload" style={{ marginRight: '0.5rem' }} />
                  }
                />
              )}
            </>
          )}

          {isUserInfoLoading ? (
            <Skeleton shape="circle" size="3rem" className="mr-2" />
          ) : (
            <>
              <div className={classes.authInfo}>
                <p className={classes.username}>{userMetadata?.userInfo.name}</p>
                <p className={classes.usermail}>{userMetadata?.userInfo.email}</p>
              </div>
              <div>
                <div onClick={handleMenuClick} className={classes.userAvatarLogo}>
                  <img src={Avatar} alt="Avatar" />
                </div>
                <OverlayPanel ref={op}>
                  <UserDetailsOverLayMenuContent userMetadata={userMetadata} logout={logout} />
                </OverlayPanel>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default observer(Navbar);
